import { TextField, Typography, Box, Grid, IconButton, Button, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, useTheme, useMediaQuery } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import Container from "@mui/material/Container";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import StopIcon from "@mui/icons-material/Stop";
import { styled } from "@mui/material/styles";
import { useState, useEffect, useRef } from "react";
import { getUserEmail, getUserName } from "../utils/sessionCheck";
import { doc, getDoc, setDoc, updateDoc, onSnapshot, collection, query, where, getDocs } from "firebase/firestore";
import { firestore } from "../firebase.config";
import { hasSupervisor } from "../context/Api";
import CircularProgress from "@mui/material/CircularProgress";
import PageNavBar from '../components/PageNavBar';
import { calculateDistance, getUserGeolocation } from "../utils/geolocationUtils";
import Footer from "../components/Footer";
import TimePicker from "./TimePicker";
import { toast } from 'react-toastify';
import { useUserContext } from "../context/UserContext";
import TenantBanner from "../components/TenantBanner";
import Cookies from 'js-cookie';
import { FormControl, InputLabel, Select, MenuItem } from "@mui/material";
const DEFAULT_RADIUS = 100; // meters
// Common styles for floating cards (rectangular tags)
const COMMON_CARD_STYLES = {
  width: 140,
  height: 90,
  p: 1.5,
  borderRadius: 0,
  background: "rgba(255,255,255,0.06)",
  border: "1px solid rgba(255,255,255,0.35)",
  backdropFilter: "blur(6px)",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
};

// Common text styles for card labels
const COMMON_LABEL_STYLES = {
  color: "#ffffffff",
  fontWeight: 500,
  fontSize: "1rem",
};

// Common text styles for card values
const COMMON_VALUE_STYLES = {
  color: "#ffffffff",
  fontWeight: 700,
  fontSize: "1.2rem",
};

// Card configurations with their specific properties - now using viewport-relative positioning
const FLOATING_CARDS = [
  {
    id: "streak",
    position: { top: "20vh", left: "3vw" },
    fallbackPosition: { top: "5vh", left: "2vw" }, // Alternative position if collision
    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
    label: "Streak",
    value: "5 Days",
    labelStyles: {
      fontFamily: "Inter, sans-serif",
      fontWeight: 200,
      fontSize: "1.2rem",
      lineHeight: 1.2,
    },
    valueStyles: {
      fontFamily: "Inter, sans-serif",
      fontWeight: 700,
      fontSize: "1.2rem",
      lineHeight: 1.2,
    }
  },
  {
    id: "tasksDone",
    position: { top: "50vh", left: "7vw" },
    fallbackPosition: { top: "60vh", left: "5vw" },
    boxShadow: "0 8px 32px rgba(124,58,237,0.15)",
    label: "Tasks Done",
    value: "12/15"
  },
  {
    id: "productivity",
    position: { bottom: "15vh", right: "4vw" },
    fallbackPosition: { bottom: "5vh", right: "2vw" },
    boxShadow: "0 8px 32px rgba(59,130,246,0.15)",
    label: "Productivity",
    value: "85%",
    valueStyles: { fontSize: "1.4rem" }
  },
  {
    id: "focusTime",
    position: { top: "10vh", right: "15vw" },
    fallbackPosition: { top: "5vh", right: "8vw" },
    boxShadow: "0 8px 32px rgba(231,58,237,0.13)",
    label: "Focus Time",
    value: "2h 45m"
  },
  {
    id: "energy",
    position: { top: "40vh", right: "9vw" },
    fallbackPosition: { top: "20vh", right: "5vw" },
    boxShadow: "0 8px 32px rgba(231,58,237,0.13)",
    label: "Energy",
    value: "High",
    valueStyles: { fontSize: "1.4rem" }
  }
];

// Function to check if a card would overlap with the timer box
const checkCardCollision = (cardPosition, cardSize, timerBoxBounds, isTablet) => {
  const cardWidth = isTablet ? 100 : 120;
  const cardHeight = isTablet ? 100 : 120;
  
  // Convert viewport units to approximate pixels for collision detection
  const vw = window.innerWidth / 100;
  const vh = window.innerHeight / 100;
  
  let cardTop, cardLeft, cardRight, cardBottom;
  
  if (cardPosition.top) {
    cardTop = parseFloat(cardPosition.top) * vh;
    cardBottom = cardTop + cardHeight;
  } else if (cardPosition.bottom) {
    cardBottom = window.innerHeight - (parseFloat(cardPosition.bottom) * vh);
    cardTop = cardBottom - cardHeight;
  }
  
  if (cardPosition.left) {
    cardLeft = parseFloat(cardPosition.left) * vw;
    cardRight = cardLeft + cardWidth;
  } else if (cardPosition.right) {
    cardRight = window.innerWidth - (parseFloat(cardPosition.right) * vw);
    cardLeft = cardRight - cardWidth;
  }
  
  // Check collision with timer box
  return !(cardLeft > timerBoxBounds.right || 
           cardRight < timerBoxBounds.left || 
           cardTop > timerBoxBounds.bottom || 
           cardBottom < timerBoxBounds.top);
};

// Function to get timer box bounds
const getTimerBoxBounds = (isMobile, isTablet) => {
  if (isMobile) return null; // No collision detection on mobile
  
  const vw = window.innerWidth / 100;
  const vh = window.innerHeight / 100;
  
  // Calculate timer box position and size based on responsive values
  let boxWidth, boxHeight, boxLeft, boxTop;
  
  if (isTablet) {
    boxWidth = window.innerWidth * 0.7; // 70% width
    boxHeight = 400; // minHeight
    boxLeft = (window.innerWidth - boxWidth) / 2;
    boxTop = window.innerHeight * 0.5 - boxHeight / 2; // Centered
  } else {
    boxWidth = window.innerWidth * 0.4; // 40% width
    boxHeight = 400; // minHeight
    boxLeft = (window.innerWidth - boxWidth) / 2;
    boxTop = window.innerHeight * 0.5 - boxHeight / 2; // Centered
  }
  
  return {
    left: boxLeft - 20, // Add some buffer
    right: boxLeft + boxWidth + 20,
    top: boxTop - 20,
    bottom: boxTop + boxHeight + 20
  };
};

const CustomOutlinedIconButton = styled(IconButton)(({ theme }) => ({
  border: `1px solid ${theme.palette.text.primary}`,
  "&:hover": {
    backgroundColor: "transparent",
  },
}));

const NewTimer = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const [elapsedTime, setElapsedTime] = useState(0);
  const [timerRunning, setTimerRunning] = useState(false);
  const [startButtonDisabled, setStartButtonDisabled] = useState(false);
  const [taskError, setTaskError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isOutsideRadius, setIsOutsideRadius] = useState(false);
  const [geoCheckDone, setGeoCheckDone] = useState(false);
  const [geolocationAuthEnabled, setGeolocationAuthEnabled] = useState(true);
  const [startTimestamp, setStartTimestamp] = useState(null);
  const [taskDescription, setTaskDescription] = useState("");
  const [isTaskValid, setIsTaskValid] = useState(false);

  const { tenantId, selectedTenantId, userRole } = useUserContext();
  const { userId } = location.state || {};

  // Add new state for stopping in progress
  const [isStopping, setIsStopping] = useState(false);
  // Snackbar and dialog state
  const [showWarning, setShowWarning] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingAutoStop, setPendingAutoStop] = useState(false);
  
  // State for responsive card positioning
  const [cardVisibility, setCardVisibility] = useState({});
  
  // Cookie keys
  const COOKIE_KEYS = {
    TIMER_DATA: 'timer_data'
  };

  // User info
  const userEmail = getUserEmail();
  const userName = getUserName();

  // Location check interval ref
  const locationCheckIntervalRef = useRef(null);
  const locationCheckFrequency = parseInt(process.env.REACT_APP_LOCATION_CHECK_FREQUENCY) || 30; // Default to 30 minutes if not set

  // // State to store user coordinates from Firestore
  // const [teamCoordinates, setteamCoordinates] = useState(null);
  
  // Use ref for latest coordinates to avoid stale closures in interval
  const teamCoordinatesRef = useRef(null);

  //FOR SELECTING TEAM AND COORDINATES
  const [teams, setTeams] = useState([]); // all teams of user
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [teamCoordinates, setTeamCoordinates] = useState(null);

  // Update ref whenever coordinates change
  useEffect(() => {
    teamCoordinatesRef.current = teamCoordinates;
  }, [teamCoordinates]);

  // Real-time listener for user coordinates - ONLY STORES COORDINATES, NO CHECKS
  useEffect(() => {
  if (!userEmail) return;

  const userRef = doc(firestore, "Users", userEmail);

  const unsubscribe = onSnapshot(userRef, async (snap) => {
    if (!snap.exists()) return;

    const userData = snap.data();

    if (!userData.teamIds) return;
    const teamList = [];

    for (const teamId of userData.teamIds) {
      const groupRef = doc(firestore, "Groups", teamId);
      const groupSnap = await getDoc(groupRef);

      if (groupSnap.exists()) {
        teamList.push({
          id: teamId,
          ...groupSnap.data(),
        });
      }
    }

    setTeams(teamList);

    // Auto-select if only one team
    if (teamList.length === 1) {
      const team = teamList[0];
      setSelectedTeam(team.id);
      setTeamCoordinates(resolveTeamCoordinates(team));
    }

    if (teamList.length > 1 && !selectedTeam) {
      const team = teamList[0];
      setSelectedTeam(team.id);
      setTeamCoordinates(resolveTeamCoordinates(team));
    }
  });

  return () => unsubscribe();
}, [userEmail, selectedTeam]);

  // Handle page refresh/closing
  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (timerRunning) {
        event.preventDefault();
        event.returnValue = "Do you really want to refresh the page? Your timer is running.";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [timerRunning]);

  // Handle window resize for responsive card positioning
  useEffect(() => {
    const handleResize = () => {
      if (!isMobile) {
        const timerBoxBounds = getTimerBoxBounds(isMobile, isTablet);
        const newVisibility = {};
        
        FLOATING_CARDS.forEach(card => {
          const wouldCollide = timerBoxBounds && checkCardCollision(
            card.position,
            { width: isTablet ? 100 : 120, height: isTablet ? 100 : 120 },
            timerBoxBounds,
            isTablet
          );
          
          if (wouldCollide && card.fallbackPosition) {
            const fallbackCollision = checkCardCollision(
              card.fallbackPosition,
              { width: isTablet ? 100 : 120, height: isTablet ? 100 : 120 },
              timerBoxBounds,
              isTablet
            );
            newVisibility[card.id] = !fallbackCollision;
          } else {
            newVisibility[card.id] = !wouldCollide;
          }
        });
        
        setCardVisibility(newVisibility);
      }
    };

    // Initial calculation
    handleResize();
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMobile, isTablet]);

  // Load timer from cookies on mount
  useEffect(() => {
    const timerData = Cookies.get(COOKIE_KEYS.TIMER_DATA);
    if (timerData) {
      try {
        const parsedData = JSON.parse(timerData);
        if (parsedData.isRunning) {
          const savedStartTime = new Date(parsedData.startTime);
          const now = new Date();
          const elapsedSeconds = Math.floor((now.getTime() - savedStartTime.getTime()) / 1000);
          
          setStartTimestamp(savedStartTime);
          setTimerRunning(true);
          setElapsedTime(elapsedSeconds);
          setTaskDescription(parsedData.taskDescription || "");
        }
      } catch (error) {
        console.error("Error loading timer:", error);
        Cookies.remove(COOKIE_KEYS.TIMER_DATA);
      }
    }
  }, []);

  // Timer interval logic
  useEffect(() => {
    let interval;
    if (timerRunning && startTimestamp) {
      interval = setInterval(() => {
        const now = new Date();
        const elapsedSeconds = Math.floor((now.getTime() - startTimestamp.getTime()) / 1000);
        setElapsedTime(elapsedSeconds);
        
        // Check for event end time auto-stop
        const qrEventStr = sessionStorage.getItem('activeQREvent');
        if (qrEventStr) {
          try {
            const event = JSON.parse(qrEventStr);
            if (event.date && event.endTime) {
              // Handle 24-hour format properly for event end time
              const eventEndTime = new Date(`${event.date}T${event.endTime}:00`);
              
              // If the event spans across midnight, we need to check if current time is past midnight
              const eventDate = new Date(event.date);
              const currentDate = new Date(now.toDateString());
              
              // If current date is different from event date and we're past midnight,
              // the event might have ended yesterday
              if (currentDate > eventDate) {
                console.log('Event end time reached (next day), auto-stopping timer');
                stopTimer(true, 'event_end');
                return;
              }
              
              if (now >= eventEndTime) {
                console.log('Event end time reached, auto-stopping timer');
                stopTimer(true, 'event_end'); // Pass 'event_end' for event end auto-stop
                return;
              }
            }
          } catch (error) {
            console.error('Error parsing QR event data:', error);
          }
        }
        
        // Auto-stop after configured hours (fallback)
        const autostopHours = parseInt(process.env.REACT_APP_AUTOSTOP_TIME_HOURS || '4', 10);
        const autostopSeconds = autostopHours * 3600;
        if (elapsedSeconds >= autostopSeconds) {
          stopTimer(true, 'timeout'); // Pass 'timeout' for time-based auto-stop
        }
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [timerRunning, startTimestamp]);

  // Periodic location check when timer is running - ONLY CHECKS AT INTERVALS
  useEffect(() => {
    if (timerRunning && geolocationAuthEnabled) {
      console.log(`Starting location check every ${locationCheckFrequency} minutes`);
      
      // Perform immediate location check when timer starts (only for initial verification)
      performLocationCheck();
      
      // Start location check interval
      locationCheckIntervalRef.current = setInterval(() => {
        console.log('Performing scheduled periodic location check...');
        performLocationCheck();
      }, locationCheckFrequency * 60 * 1000); // Convert minutes to milliseconds

      // Cleanup function
      return () => {
        if (locationCheckIntervalRef.current) {
          clearInterval(locationCheckIntervalRef.current);
          locationCheckIntervalRef.current = null;
        }
      };
    } else {
      // Clear interval when timer stops or geolocation is disabled
      if (locationCheckIntervalRef.current) {
        clearInterval(locationCheckIntervalRef.current);
        locationCheckIntervalRef.current = null;
      }
    }
  }, [timerRunning, geolocationAuthEnabled, locationCheckFrequency]);

  // Function to perform location check and stop timer if outside radius (uses QR event coords if present)
  const performLocationCheck = async () => {
    if (!timerRunning || !geolocationAuthEnabled) {
      console.log('Timer not running or geolocation disabled, skipping location check');
      return;
    }

    try {
      console.log('Starting scheduled location check...');
      // Prefer active QR event coordinates if available
      let currentCoordinates = null;
      const qrEventStr = sessionStorage.getItem('activeQREvent');
      if (qrEventStr) {
        try {
          const e = JSON.parse(qrEventStr);
          // If event date/time passed, clear it and fallback to user coords
          const now = new Date();
          const end = e?.date && e?.endTime ? new Date(`${e.date}T${e.endTime}:00`) : null;
          if (!end || now <= end) {
            currentCoordinates = { latitude: e.latitude, longitude: e.longitude, radius: e.radius };
          } else {
            sessionStorage.removeItem('activeQREvent');
            sessionStorage.removeItem('activeQREventId');
          }
        } catch {}
      }
      if (!currentCoordinates) {
        currentCoordinates = teamCoordinatesRef.current;
      }
      if (!currentCoordinates) {
        console.log('No coordinates available for location check');
        return;
      }

      const { latitude, longitude, radius } = currentCoordinates;
      console.log('Using LATEST coordinates from Firestore for scheduled check:', { latitude, longitude, radius });

      const userGeolocation = await getUserGeolocation();
      
      // Calculate distance
      const distance = calculateDistance(
        userGeolocation.latitude, 
        userGeolocation.longitude, 
        latitude, 
        longitude
      );
      
      // Calculate accuracy buffer (10% of radius or 10m, whichever is larger)
      const accuracyBuffer = Math.max(radius * 0.1, 10);
      
      // Calculate the maximum allowed distance
      const maxAllowedDistance = radius + accuracyBuffer;
      
      // Get the minimum possible distance (distance minus accuracy)
      const minPossibleDistance = Math.max(0, distance - userGeolocation.accuracy);
      
      // User is outside if the minimum possible distance is greater than max allowed
      const isOutside = minPossibleDistance > maxAllowedDistance;
      
      console.log(
        `Scheduled Location Check: Distance: ${distance.toFixed(1)}m, ` +
        `Min Possible Distance: ${minPossibleDistance.toFixed(1)}m, ` +
        `Allowed Radius: ${radius}m, ` +
        `Buffer: ${accuracyBuffer.toFixed(1)}m, ` +
        `Max Allowed: ${maxAllowedDistance.toFixed(1)}m, ` +
        `Accuracy: ${userGeolocation.accuracy.toFixed(1)}m, ` +
        `Outside: ${isOutside}`
      );

      if (isOutside) {
        // User is outside allowed radius, stop the timer immediately
        console.log('User outside allowed radius, stopping timer immediately...');
        toast.error("Location verification failed - timer stopped");
        await stopTimer(true, 'location_mismatch'); // Pass 'location_mismatch' for location-based auto-stop
      } else {
        console.log('User location verified - within allowed radius');
        // Update UI to show user is within radius
        setIsOutsideRadius(false);
        setGeoCheckDone(true);
      }
    } catch (error) {
      console.error("Location check failed:", error);
      // On location check failure, stop the timer for security
      toast.error("Location verification failed - timer stopped");
      await stopTimer(true, 'location_mismatch');
    }
  };

  // Check and reset stale timers on mount
  useEffect(() => {
    const checkStaleTimers = async () => {
      if (!userEmail) return;
      
      try {
        const userTimerRef = doc(firestore, "UserTimers", userEmail);
        const userTimerDoc = await getDoc(userTimerRef);
        
        if (userTimerDoc.exists() && userTimerDoc.data().isRunning) {
          const startTime = new Date(userTimerDoc.data().startTime);
          const hoursRunning = (Date.now() - startTime.getTime()) / 3600000;
          
          // Reset timer if running for more than 12 hours
          if (hoursRunning > 12) {
            await updateDoc(userTimerRef, { isRunning: false });
            console.log("Cleared stale timer");
          }
        }
      } catch (error) {
        console.error("Stale timer check error:", error);
      }
    };

    checkStaleTimers();
  }, [userEmail, selectedTeam]);

  // Fetch geolocation settings from Firestore
  useEffect(() => {
    const fetchGeolocationSettings = async () => {
      try {
        const settingsDocRef = doc(firestore, "Settings", "geolocationAuth");
        const settingsDoc = await getDoc(settingsDocRef);
  
        if (settingsDoc.exists()) {
          const settingsData = settingsDoc.data();
          setGeolocationAuthEnabled(settingsData.enabled);
        } else {
          console.error("Geolocation settings not found.");
          setGeolocationAuthEnabled(false);
        }
      } catch (error) {
        console.error("Error fetching geolocation settings:", error);
        setGeolocationAuthEnabled(false);
      } finally {
        setIsLoading(false);
      }
    };
  
    fetchGeolocationSettings();
  }, []);

  // Initial location check when component mounts - ONLY FOR UI DISPLAY
  useEffect(() => {
    performInitialLocationCheck();
  }, [teamCoordinates]);

  // Initial location check (only for UI display, doesn't stop timer)
  const performInitialLocationCheck = async () => {
    if (!geolocationAuthEnabled) {
      setGeoCheckDone(true);
      setIsOutsideRadius(false);
      return;
    }

    try {
      // Prefer QR event coordinates if active
      let coords = null;
      const qrEventStr = sessionStorage.getItem('activeQREvent');
      if (qrEventStr) {
        try {
          const e = JSON.parse(qrEventStr);
          const now = new Date();
          const end = e?.date && e?.endTime ? new Date(`${e.date}T${e.endTime}:00`) : null;
          if (!end || now <= end) {
            coords = { latitude: e.latitude, longitude: e.longitude, radius: e.radius };
          } else {
            sessionStorage.removeItem('activeQREvent');
            sessionStorage.removeItem('activeQREventId');
          }
        } catch {}
      }
      if (!coords) {
        coords = teamCoordinates;
      }
      if (!coords) {
        setGeoCheckDone(true);
        setIsOutsideRadius(false);
        return;
      }

      const { latitude, longitude, radius } = coords;
      const userGeolocation = await getUserGeolocation();
      
      // Calculate distance
      const distance = calculateDistance(
        userGeolocation.latitude, 
        userGeolocation.longitude, 
        latitude, 
        longitude
      );
      
      // Calculate accuracy buffer (10% of radius or 10m, whichever is larger)
      const accuracyBuffer = Math.max(radius * 0.1, 10);
      
      // Calculate the maximum allowed distance
      const maxAllowedDistance = radius + accuracyBuffer;
      
      // Get the minimum possible distance (distance minus accuracy)
      const minPossibleDistance = Math.max(0, distance - userGeolocation.accuracy);
      
      // User is outside if the minimum possible distance is greater than max allowed
      const isOutside = minPossibleDistance > maxAllowedDistance;
      
      setIsOutsideRadius(isOutside);
      setGeoCheckDone(true);
      
      console.log(
        `Initial UI Location Check: Distance: ${distance.toFixed(1)}m, ` +
        `Min Possible Distance: ${minPossibleDistance.toFixed(1)}m, ` +
        `Allowed Radius: ${radius}m, ` +
        `Buffer: ${accuracyBuffer.toFixed(1)}m, ` +
        `Max Allowed: ${maxAllowedDistance.toFixed(1)}m, ` +
        `Accuracy: ${userGeolocation.accuracy.toFixed(1)}m, ` +
        `Outside: ${isOutside}`
      );
    } catch (error) {
      console.error("Initial location verification failed:", error);
      setIsOutsideRadius(false);
      setGeoCheckDone(true);
    }
  };

  const resolveTeamCoordinates = (team) => {
    if (!team) return null;

    const userEmail = getUserEmail();

    if (team.teamType === "distributed") {
      const memberCoords = team.memberCoordinates?.[userEmail];

      if (memberCoords?.latitude && memberCoords?.longitude) {
        return {
          latitude: memberCoords.latitude,
          longitude: memberCoords.longitude,
          radius: memberCoords.radius || DEFAULT_RADIUS
        };
      }
      return null;
    }

    if (team.coordinates?.latitude && team.coordinates?.longitude) {
      return {
        ...team.coordinates,
        radius: team.coordinates?.radius || DEFAULT_RADIUS
      };
    }

    return null;
  };

  // Manual location check for UI (only updates UI, doesn't stop timer)
  const checkUserDistance = async () => {
    await performInitialLocationCheck();
    toast.info("Location re-checked");
  };

  // Validate task description
  const validateTask = (task) => {
    const isValid = task.trim().length > 0;
    setIsTaskValid(isValid);
    return isValid;
  };

  // Handle task input changes
  const handleTaskChange = (e) => {
    const newTask = e.target.value;
    setTaskDescription(newTask);
    validateTask(newTask);
  };

  const selectedTeamObj = teams.find(t => t.id === selectedTeam);

  const handleTeamSelect = (teamId) => {
    setSelectedTeam(teamId);

    const team = teams.find(t => t.id === teamId);
    if (!team) return;

    setTeamCoordinates(resolveTeamCoordinates(team));
  };

  // Start timer
  const taskSubmit = async (event) => {
    event.preventDefault();
    setTaskError("");

    if (!validateTask(taskDescription)) {
      setTaskError("Please enter a task description");
      return;
    }

    try {
      // Check location before starting timer
      const qrEventStr = sessionStorage.getItem('activeQREvent');
      if (qrEventStr) {
        try {
          const event = JSON.parse(qrEventStr);
          const userGeolocation = await getUserGeolocation();
          const distance = calculateDistance(
            userGeolocation.latitude, 
            userGeolocation.longitude, 
            event.latitude, 
            event.longitude
          );
          const accuracyBuffer = Math.max((event.radius || 100) * 0.1, 10);
          const maxAllowed = (event.radius || 100) + accuracyBuffer;
          if (distance > maxAllowed) {
            setTaskError("You must be at the event location to start timer");
            return;
          }
        } catch (error) {
          console.error("Location check failed:", error);
          setTaskError("Location verification failed");
          return;
        }
      }

      // Check if user already has an active timer
      const userTimerRef = doc(firestore, "UserTimers", userEmail);
      const userTimerDoc = await getDoc(userTimerRef);
      
      if (userTimerDoc.exists() && userTimerDoc.data().isRunning) {
        toast.error("Timer already running on another device");
        return;
      }

      if (!selectedTeam) {
        alert("Please select a team first");
        return;
      }

      if (!teamCoordinates?.latitude || !teamCoordinates?.longitude) {
        alert("Invalid team coordinates");
        return;
      }

      const startTime = new Date();

      // Write to Firebase
      await setDoc(userTimerRef, {
        userEmail,
        userName,
        startTime: startTime.toISOString(),
        isRunning: true,
        taskDescription: taskDescription.trim(),
        teamId: selectedTeam,
        teamName: selectedTeamObj?.teamName || null,
        qrEventId: (sessionStorage.getItem('activeQREventId') || null)
      });

      // Save to cookies
      Cookies.set(COOKIE_KEYS.TIMER_DATA, JSON.stringify({
        isRunning: true,
        startTime: startTime.toISOString(),
        taskDescription: taskDescription.trim()
      }), { 
        expires: 1,
        secure: process.env.NODE_ENV === 'production'
      });

      setStartTimestamp(startTime);
      setTimerRunning(true);
      if (geolocationAuthEnabled) {
        await performLocationCheck();
      }
      setStartButtonDisabled(true);
      setSuccessMessage("Timer started!");

    } catch (error) {
      console.error("Start error:", error);
      toast.error("Failed to start timer");
    }
  };

  // Stop timer with Snackbar and Dialog confirmation
  const stopTimer = async (isAutoStop = false, autoStopReason = null) => {
    if (isStopping) return;
    if (!isAutoStop) {
      setShowWarning(true);
      setShowConfirmDialog(true);
      setPendingAutoStop(false);
      return;
    }
    // For auto-stop, proceed directly
    await handleStopConfirmed(true, autoStopReason);
  };

  // Helper function to check if user has a supervisor
  const checkUserHasSupervisor = async (email) => {
    try {
      return await hasSupervisor(email);
    } catch (error) {
      console.error("Error checking supervisor:", error);
      // If error, assume no supervisor for safety (auto-approve)
      return false;
    }
  };

  // Confirm dialog handler
  const handleStopConfirmed = async (isAutoStop = false, autoStopReason = null) => {
    setShowConfirmDialog(false);
    setShowWarning(false);
    setIsStopping(true);
    try {
      const endTime = new Date();
      const finalTenantId = userRole === "SuperAdmin"
        ? selectedTenantId || sessionStorage.getItem("selectedTenantId")
        : tenantId || sessionStorage.getItem("selectedTenantId");
      if (!finalTenantId) {
        throw new Error("Missing tenant ID");
      }
      // Calculate total minutes (rounded to nearest minute)
      const totalTimeBooked = Math.round(elapsedTime / 60);
      // Create document ID with email + timestamp
      const docId = `${userEmail}_${startTimestamp.getTime()}`;
      
      // Auto-approve logic:
      // 1. Admin role auto-approves (SuperAdmin excluded from transactions)
      // 2. Users without supervisors auto-approve (independent users/supervisors)
      // 3. Users with supervisors require approval
      const isAdmin = userRole === "admin" && userRole !== "SuperAdmin";
      const userHasSupervisor = await checkUserHasSupervisor(userEmail);
      const shouldAutoApprove = isAdmin || !userHasSupervisor;
      
      // Determine what to save based on stop reason
      if (!isAutoStop) {
        // Manual stop - save normally
        const approvalStatus = shouldAutoApprove ? "approved" : "pending";
        const timerData = {
          date: getUTCDate(endTime),
          startTime: getTime(startTimestamp),
          endTime: getTime(endTime),
          totalTimeBooked,
          userEmail,
          userName,
          taskDescription: taskDescription.trim(),
          tenantId: finalTenantId,
          teamId: selectedTeam || null,
          teamName: selectedTeamObj?.teamName || null,
          status: "completed",
          approvalStatus: approvalStatus,
          createdAt: new Date().toISOString()
        };
        
        if (shouldAutoApprove) {
          timerData.approvedAt = new Date().toISOString();
        }
        
        await setDoc(doc(firestore, "Timer", docId), timerData);
      } else if (autoStopReason === 'event_end') {
        // Event end auto-stop - store as "not set"
        // Auto-approve: Admin OR users without supervisors
        const isAdmin = userRole === "admin" && userRole !== "SuperAdmin";
        const userHasSupervisor = await checkUserHasSupervisor(userEmail);
        const shouldAutoApprove = isAdmin || !userHasSupervisor;
        const approvalStatus = shouldAutoApprove ? "approved" : "pending";
        
        const timerData = {
          date: getUTCDate(endTime),
          startTime: getTime(startTimestamp),
          endTime: getTime(endTime),
          totalTimeBooked,
          userEmail,
          userName,
          taskDescription: "Auto-stopped at event end time",
          tenantId: finalTenantId,
          teamId: selectedTeam || null,
          teamName: selectedTeamObj?.teamName || null,
          status: "not set",
          autoStopReason: "event_end",
          approvalStatus: approvalStatus,
          createdAt: new Date().toISOString()
        };
        
        if (shouldAutoApprove) {
          timerData.approvedAt = new Date().toISOString();
        }
        
        await setDoc(doc(firestore, "Timer", docId), timerData);
      } else if (autoStopReason === 'location_mismatch') {
        // Location mismatch auto-stop - store with location info
        // Auto-approve: Admin OR users without supervisors
        const isAdmin = userRole === "admin" && userRole !== "SuperAdmin";
        const userHasSupervisor = await checkUserHasSupervisor(userEmail);
        const shouldAutoApprove = isAdmin || !userHasSupervisor;
        const approvalStatus = shouldAutoApprove ? "approved" : "pending";
        
        const timerData = {
          date: getUTCDate(endTime),
          startTime: getTime(startTimestamp),
          endTime: getTime(endTime),
          totalTimeBooked,
          userEmail,
          userName,
          taskDescription: "Auto-stopped due to location mismatch",
          tenantId: finalTenantId,
          teamId: selectedTeam || null,
          teamName: selectedTeamObj?.teamName || null,
          status: "location_mismatch",
          autoStopReason: "location_mismatch",
          approvalStatus: approvalStatus,
          createdAt: new Date().toISOString()
        };
        
        if (shouldAutoApprove) {
          timerData.approvedAt = new Date().toISOString();
        }
        
        await setDoc(doc(firestore, "Timer", docId), timerData);
      }
      // For other auto-stops (timeout), don't save to Timer collection
      
      // Update UserTimers collection
      const userTimerRef = doc(firestore, "UserTimers", userEmail);
      await updateDoc(userTimerRef, {
        isRunning: false,
        ...(isAutoStop && { 
          status: autoStopReason === 'event_end' ? "not set" : "auto-stopped",
          autoStopReason: autoStopReason
        })
      });
      // Clear cookies and state
      Cookies.remove(COOKIE_KEYS.TIMER_DATA);
      // Clear active QR event session
      sessionStorage.removeItem('activeQREvent');
      sessionStorage.removeItem('activeQREventId');
      setTimerRunning(false);
      setStartButtonDisabled(false);
      setTaskDescription("");
      
      // Show appropriate message based on stop reason
      if (isAutoStop) {
        if (autoStopReason === 'event_end') {
          toast.info("Timer auto-stopped at event end time");
        } else if (autoStopReason === 'location_mismatch') {
          toast.error("Timer stopped due to location verification failure");
        } else {
          toast.info("Timer stopped automatically");
        }
      } else {
        setSuccessMessage("Timer stopped successfully!");
      }
    } catch (error) {
      console.error("Stop error:", error);
      toast.error("Failed to stop timer");
    } finally {
      setIsStopping(false);
    }
  };

  // Time formatting helpers
  const formatTime = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return `${h}:${m < 10 ? '0' : ''}${m}:${s < 10 ? '0' : ''}${s}`;
  };

  // UTC date formatting (DD-MM-YY)
  const getUTCDate = (date) => {
    const day = date.getUTCDate().toString().padStart(2, '0');
    const month = (date.getUTCMonth() + 1).toString().padStart(2, '0');
    const year = date.getUTCFullYear().toString().slice(-2);
    return `${day}-${month}-${year}`;
  };

  // Time formatting (HH:MM:SS AM/PM)
  const getTime = (date) => {
    const hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const seconds = date.getSeconds().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const formattedHours = hours % 12 || 12; // Convert to 12-hour format
    return `${formattedHours}:${minutes}:${seconds} ${ampm}`;
  };

  return (
    <Box sx={{ 
      minHeight: "100vh",
      background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      display: "flex",
      flexDirection: "column",
      position: "relative",
      overflow: "hidden",
      px: isMobile ? 2 : 0,
      py: isMobile ? 1 : 0,
    }}>
      <PageNavBar title="Timer" backTo="/dashboard" />
      
      <TenantBanner />
      
      {/* Floating Message Cards - Hidden on mobile for better visibility */}
      {!isMobile && FLOATING_CARDS.map((card) => {
        // Skip rendering if card should be hidden due to collision
        if (!cardVisibility[card.id]) return null;
        
        const timerBoxBounds = getTimerBoxBounds(isMobile, isTablet);
        
        // Check if card would collide with timer box
        const wouldCollide = timerBoxBounds && checkCardCollision(
          card.position, 
          { width: isTablet ? 100 : 120, height: isTablet ? 100 : 120 }, 
          timerBoxBounds, 
          isTablet
        );
        
        // If collision detected, try fallback position
        let finalPosition = card.position;
        if (wouldCollide && card.fallbackPosition) {
          const fallbackCollision = checkCardCollision(
            card.fallbackPosition,
            { width: isTablet ? 100 : 120, height: isTablet ? 100 : 120 },
            timerBoxBounds,
            isTablet
          );
          if (!fallbackCollision) {
            finalPosition = card.fallbackPosition;
          }
        }
        
        return (
          <Box
            key={card.id}
            sx={{
              position: "absolute",
              ...(isTablet ? {
                // Tablet positioning - smaller cards
                ...COMMON_CARD_STYLES,
                width: 100,
                height: 100,
                p: 1.5,
                ...finalPosition,
              } : {
                // Desktop positioning - original sizes
                ...COMMON_CARD_STYLES,
                ...finalPosition,
              }),
              boxShadow: card.boxShadow,
              zIndex: 1,
              // Add responsive adjustments for very small screens
              ...(theme => ({
                '@media (max-height: 600px)': {
                  width: isTablet ? 80 : 100,
                  height: isTablet ? 80 : 100,
                  p: isTablet ? 1 : 1.5,
                  '& .MuiTypography-root': {
                    fontSize: isTablet ? '0.8rem' : '0.9rem',
                  }
                },
                '@media (max-height: 500px)': {
                  width: isTablet ? 70 : 90,
                  height: isTablet ? 70 : 90,
                  p: isTablet ? 0.5 : 1,
                  '& .MuiTypography-root': {
                    fontSize: isTablet ? '0.7rem' : '0.8rem',
                  }
                }
              }))
            }}
          >
            <Typography
              sx={{
                ...COMMON_LABEL_STYLES,
                ...(card.labelStyles || {}),
                fontSize: isTablet ? '0.9rem' : '1rem',
              }}
            >
              {card.label}
            </Typography>

            <Typography
              sx={{
                ...COMMON_VALUE_STYLES,
                ...(card.valueStyles || {}),
                fontSize: isTablet ? '1.1rem' : '1.2rem',
              }}
            >
              {card.value}
            </Typography>
          </Box>
        );
      })}
      
      <Container sx={{ 
        pb: isMobile ? 4 : 8, 
        flex: 1, 
        display: "flex", 
        flexDirection: "column", 
        justifyContent: "center",
        alignItems: "center",
        px: isMobile ? 4 : 3,
      }}>
        
        <Box
        sx={(theme) => ({
          backgroundColor: "white",
          borderRadius: { xs: 6, sm: 8, md: 10 },
          p: { xs: 3, md: 4 }, // smaller padding on mobile
          boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
          width: {
            xs: "calc(100% - 49px)", // mobile
            sm: "70%",               // tablet
            md: "40%",               // laptop/desktop
          },
          maxWidth: {
            xs: "calc(100vw - 49px)", // mobile
            md: 900,                  // laptop cap
          },
          minHeight: { xs: 350, md: 400 },
          mx: "auto",
          mt: { xs: 2, md: 4 },
          alignSelf: "center",
        })}
      >


        <Box sx={{ textAlign: "center", mt: isMobile ? 1 : 2 }}>
          <Typography 
            variant={isMobile ? "body1" : "h6"}
            sx={{ 
              color: "#6b7280",
              fontSize: isMobile ? "1rem" : "1.2rem",
              fontWeight: 600,
              marginBottom: 1,
              textAlign: "center",
              fontFamily: "Roboto, sans-serif"
            }}
          >
            Local Time:
          </Typography>
          <TimePicker />
        </Box>

        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: isMobile ? 1 : 2 }}>
            <CircularProgress sx={{ color: "black" }} />
          </Box>
        ) : (
          <Box component="form" onSubmit={taskSubmit} sx={{ textAlign: "center", mt: isMobile ? 2 : 4 }}>
            <Typography 
              variant={isMobile ? "body1" : "h6"}
              sx={{ 
                color: "#6b7280",
                fontSize: isMobile ? "1rem" : "1.2rem",
                fontWeight: 600,
                marginBottom: 1,
                textAlign: "center"
              }}
            >
              Timer:
            </Typography>
            
            <Typography 
              sx={{ 
                color: "#1f2937",
                fontSize: isMobile ? "2rem" : "2.5rem",
                fontWeight: 700,
                marginBottom: isMobile ? 1.5 : 2,
                fontFamily: "Roboto, sans-serif",
                textShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                textAlign: "center"
              }}
            >
              {formatTime(elapsedTime)}
            </Typography>

            {teams.length > 0 && (
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Select Team</InputLabel>
                <Select
                  value={selectedTeam || ""}
                  label="Select Team"
                  disabled={teams.length === 1 || timerRunning}
                  onChange={(e) => {
                    handleTeamSelect(e.target.value);
                  }}
                >
                  {teams.map((team) => (
                    <MenuItem key={team.id} value={team.id}>
                      {team.teamName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {selectedTeamObj && (
              <>
                {console.log("Coordinates:", selectedTeamObj.coordinates)}
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Working with: <b>{selectedTeamObj.teamName}</b>
                </Typography>
              </>
            )}
            {selectedTeam && !teamCoordinates && (
              <Typography color="error" variant="body2" sx={{ mb: 1 }}>
                No coordinates assigned to this team
              </Typography>
            )}

            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              mt: isMobile ? 1.5 : 2, 
              mb: isMobile ? 1.5 : 2 
            }}>
              <TextField
                label="Task description"
                variant="outlined"
                fullWidth
                value={taskDescription}
                onChange={handleTaskChange}
                multiline
                minRows={isMobile ? 1 : 1}
                maxRows={isMobile ? 3 : 4}
                sx={{
                  '& .MuiInputLabel-root': {
                    color: 'black',
                    fontSize: isMobile ? '0.875rem' : '1rem',
                  },
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    fontSize: isMobile ? '0.875rem' : '1rem',
                    '& fieldset': {
                      borderColor: 'rgba(0, 0, 0, 0.3)',
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(0, 0, 0, 0.5)',
                    },
                  },
                }}
              />
            </Box>

            {!geoCheckDone ? (
              <Typography sx={{ fontSize: isMobile ? '0.875rem' : '1rem' }}>
                Checking location...
              </Typography>
            ) : isOutsideRadius ? (
              <Box sx={{ 
                backgroundColor: '#fff8e1', 
                p: isMobile ? 1.5 : 2, 
                borderRadius: 1,
                mt: isMobile ? 1.5 : 2,
                textAlign: 'left'
              }}>
                <Typography 
                  variant={isMobile ? "body2" : "subtitle1"} 
                  color="error" 
                  gutterBottom
                  sx={{ fontSize: isMobile ? '0.875rem' : '1rem' }}
                >
                  You're outside the allowed work area
                </Typography>
                <Typography 
                  variant="body2"
                  sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}
                >
                  • Your current location is outside the permitted work radius<br />
                  • Please return to your designated work area to use the timer
                </Typography>
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={() => {
                    toast.info("Re-checking your location...");
                    checkUserDistance();
                  }}
                  sx={{ 
                    mt: 1,
                    fontSize: isMobile ? '0.75rem' : '0.875rem',
                    py: isMobile ? 0.5 : 1,
                    px: isMobile ? 1.5 : 2,
                  }}
                >
                  Re-check Location
                </Button>
              </Box>
            ) : (
              <>
                {taskError && (
                  <Typography sx={{ 
                    mb: isMobile ? 1.5 : 2, 
                    color: "#ffebee", 
                    fontWeight: "bold",
                    fontSize: isMobile ? '0.875rem' : '1rem'
                  }}>
                    {taskError}
                  </Typography>
                )}
                <Grid container spacing={isMobile ? 1.5 : 2} justifyContent="center">
                  <Grid item>
                    <CustomOutlinedIconButton
                      type="submit"
                      disabled={timerRunning || !isTaskValid}
                      sx={{
                        color: "#ffffff",
                        borderColor: "#2e7d32",
                        backgroundColor: "#2e7d32",
                        width: isMobile ? 48 : 56,
                        height: isMobile ? 48 : 56,
                        "&:hover": {
                          backgroundColor: "#1b5e20",
                          borderColor: "#1b5e20",
                          color: "#ffffff",
                        },
                        "&:disabled": {
                          color: "rgba(255, 255, 255, 0.3)",
                          borderColor: "rgba(46, 125, 50, 0.3)",
                          backgroundColor: "rgba(46, 125, 50, 0.3)",
                        }
                      }}
                    >
                      <PlayArrowIcon sx={{ 
                        color: "#ffffff",
                        fontSize: isMobile ? '1.5rem' : '2rem'
                      }} />
                    </CustomOutlinedIconButton>
                  </Grid>
                  <Grid item>
                    <CustomOutlinedIconButton
                      onClick={() => stopTimer(false)}
                      disabled={!timerRunning}
                      sx={{
                        color: "#ffffff",
                        borderColor: "#b71c1c",
                        backgroundColor: "#b71c1c",
                        width: isMobile ? 48 : 56,
                        height: isMobile ? 48 : 56,
                        "&:hover": {
                          backgroundColor: "#d32f2f",
                          borderColor: "#d32f2f",
                          color: "#ffffff",
                        },
                        "&:disabled": {
                          color: "rgba(255, 255, 255, 0.3)",
                          borderColor: "rgba(183, 28, 28, 0.3)",
                          backgroundColor: "rgba(183, 28, 28, 0.3)",
                        }
                      }}
                    >
                      <StopIcon sx={{ 
                        color: "#ffffff",
                        fontSize: isMobile ? '1.5rem' : '2rem'
                      }} />
                    </CustomOutlinedIconButton>
                  </Grid>
                </Grid>
              </>
            )}
            
            {successMessage && (
              <Typography sx={{ 
                mt: isMobile ? 1.5 : 2, 
                color: "#c8e6c9", 
                fontWeight: "bold",
                fontSize: isMobile ? '0.875rem' : '1rem'
              }}>
                {successMessage}
              </Typography>
            )}
            
            {/* Dialog for confirmation */}
            <Dialog
              open={showConfirmDialog}
              onClose={() => setShowConfirmDialog(false)}
              aria-labelledby="stop-timer-dialog-title"
              maxWidth="xs"
              fullWidth
              PaperProps={{
                sx: {
                  borderRadius: { xs: 2, sm: 3 },
                  minWidth: { xs: '90vw', sm: 320 },
                  maxWidth: { xs: '95vw', sm: 400 },
                  mx: { xs: 2, sm: 'auto' },
                }
              }}
            >
              <DialogTitle 
                id="stop-timer-dialog-title"
                sx={{ 
                  pb: { xs: 1, sm: 1.5 },
                  fontSize: { xs: '1rem', sm: '1.25rem' },
                  textAlign: 'center',
                  fontWeight: 600
                }}
              >
                Stop Timer?
              </DialogTitle>
              <DialogContent sx={{ px: { xs: 2, sm: 3 }, py: { xs: 1, sm: 1.5 } }}>
                <DialogContentText 
                  sx={{ 
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    textAlign: 'center',
                    mb: 0,
                    lineHeight: 1.4
                  }}
                >
                  When you stop, your current task details will be saved. Would you like to review or update your task before stopping?                </DialogContentText>
              </DialogContent>
              <DialogActions sx={{ 
                flexDirection: { xs: 'column', sm: 'row' },
                gap: { xs: 1, sm: 1.5 },
                px: { xs: 2, sm: 3 },
                pb: { xs: 2, sm: 2.5 },
                pt: 0
              }}>
                <Button 
                  onClick={() => setShowConfirmDialog(false)} 
                  color="primary" 
                  variant="outlined"
                  fullWidth={isMobile}
                  size={isMobile ? "large" : "medium"}
                  sx={{ 
                    fontSize: { xs: '0.875rem', sm: '0.9rem' },
                    py: { xs: 0.75, sm: 0.5 },
                    px: { xs: 2, sm: 2.5 },
                    minHeight: { xs: 44, sm: 36 }
                  }}
                >
                  Review Task
                </Button>
                <Button 
                  onClick={() => handleStopConfirmed(false)} 
                  color="error" 
                  variant="contained"
                  fullWidth={isMobile}
                  size={isMobile ? "large" : "medium"}
                  sx={{ 
                    fontSize: { xs: '0.875rem', sm: '0.9rem' },
                    py: { xs: 0.75, sm: 0.5 },
                    px: { xs: 2, sm: 2.5 },
                    minHeight: { xs: 44, sm: 36 }
                  }}
                >
                  Yes, Stop
                </Button>
              </DialogActions>
            </Dialog>
          </Box>
        )}
      </Box>
      <Footer />
        </Container>
      </Box>
    );
  };

export default NewTimer;